import { DateTime } from "luxon";

export const DAY_MILLIS = 24 * 3600 * 1000;

export function parseIsoDate(dateStr: string, timeZone = "UTC"): Date {
  return parseIsoOrThrow(dateStr, timeZone);
}

export function parseIsoDateTime(dateStr: string): Date {
  return parseIsoOrThrow(dateStr);
}

export function formatIsoDateTime(date: Date, timeZone?: string): string {
  return checkDateTime(
    DateTime.fromJSDate(date).setZone(timeZone ?? "UTC"),
  ).toISO();
}

export function checkDateTime(date: DateTime): DateTime<true> {
  if (!date.isValid) {
    throw new Error(
      `Invalid date: ${date.invalidReason} (${date.invalidExplanation})`,
    );
  }
  return date;
}

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function parseIsoOrThrow(dateStr: string, timeZone = "UTC"): Date {
  const res = DateTime.fromISO(dateStr, { zone: timeZone });
  if (!res.isValid) {
    throw new Error(
      `Failed to parse string: '${dateStr}' (${res.invalidReason}: ${res.invalidExplanation}`,
    );
  }
  return res.toJSDate();
}

export enum DateStyle {
  YEAR = "YEAR",
  MONTH = "MONTH",
  YEAR_MONTH = "YEAR_MONTH",
  YEAR_MONTH_WORD = "YEAR_MONTH_WORD",
  YEAR_MONTH_WORD_SHORT = "YEAR_MONTH_WORD_SHORT",
  DATE = "DATE",
  DATE_WEEKDAY = "DATE_WEEKDAY",
  DATE_MONTH_WORD_SHORT_YEAR = "DATE_MONTH_WORD_SHORT_YEAR",
  DATE_WEEKDAY_SHORT = "DATE_WEEKDAY_SHORT",
  DATE_MONTH_SHORT = "DATE_MONTH_SHORT",
  DATE_MONTH_NUM = "DATE_MONTH_NUM",
  WEEKDAY = "WEEKDAY",
  WEEKDAY_INITIAL = "WEEKDAY_INITIAL",
  WEEKDAY_SHORT_DATE_MONTH_SHORT = "WEEKDAY_SHORT_DATE_MONTH_SHORT",
  WEEKDAY_DATE_MONTH_YEAR = "WEEKDAY_DATE_MONTH_YEAR",
  DATETIME = "DATETIME",
  TIME = "TIME",
  MONTH_WORD = "MONTH_WORD",
  MONTH_WORD_SHORT = "MONTH_WORD_SHORT",
}

export function formatDate(
  date: Date,
  language: string,
  style: DateStyle,
  timeZone?: string,
): string {
  const options = styleToOptions(style);
  if (timeZone !== undefined) {
    options.timeZone = timeZone;
  }
  return new Intl.DateTimeFormat(language, options).format(date);
}

export function getLastYearSameDay(date: Date): Date {
  const d = DateTime.fromJSDate(date);
  const targetWeekday = d.weekday;
  let plus = d.minus({ years: 1 });
  let minus = d.minus({ years: 1 });
  for (let i = 0; i < 6; i++) {
    plus = plus.plus({ days: 1 });
    minus = minus.minus({ days: 1 });
    if (plus.weekday === targetWeekday) return plus.toJSDate();
    if (minus.weekday === targetWeekday) return minus.toJSDate();
  }
  return d.minus({ years: 1 }).toJSDate();
}

export function dateHasTimeChange(date: Date, timeZone: string): boolean {
  const dateTime = DateTime.fromJSDate(date, { zone: timeZone });
  return dateTime.offset !== dateTime.plus({ day: 1 }).offset;
}

function styleToOptions(style: DateStyle): Intl.DateTimeFormatOptions {
  const yearNum = "numeric"; // "2-digit";
  const monthNum = "numeric"; // "2-digit";
  const dayNum = "numeric"; // "2-digit";
  switch (style) {
    case DateStyle.YEAR:
      return { year: yearNum };
    case DateStyle.MONTH:
      return { month: "long" };
    case DateStyle.YEAR_MONTH:
      return { year: yearNum, month: monthNum };
    case DateStyle.YEAR_MONTH_WORD:
      return { year: yearNum, month: "long" };
    case DateStyle.YEAR_MONTH_WORD_SHORT:
      return { year: yearNum, month: "short" };
    case DateStyle.DATE:
      return {
        year: yearNum,
        month: monthNum,
        day: dayNum,
      };
    case DateStyle.DATE_WEEKDAY:
      return {
        year: yearNum,
        month: monthNum,
        day: dayNum,
        weekday: "long",
      };
    case DateStyle.DATE_MONTH_WORD_SHORT_YEAR:
      return {
        year: yearNum,
        month: "short",
        day: dayNum,
      };
    case DateStyle.DATE_WEEKDAY_SHORT:
      return {
        year: yearNum,
        month: monthNum,
        day: dayNum,
        weekday: "short",
      };
    case DateStyle.DATE_MONTH_NUM:
      return {
        month: monthNum,
        day: dayNum,
      };
    case DateStyle.DATE_MONTH_SHORT:
      return {
        month: "short",
        day: dayNum,
      };
    case DateStyle.WEEKDAY:
      return {
        weekday: "long",
      };
    case DateStyle.WEEKDAY_INITIAL: {
      return {
        weekday: "narrow",
      };
    }
    case DateStyle.WEEKDAY_SHORT_DATE_MONTH_SHORT: {
      return {
        month: "short",
        day: dayNum,
        weekday: "short",
      };
    }
    case DateStyle.WEEKDAY_DATE_MONTH_YEAR:
      return {
        weekday: "long",
        day: dayNum,
        month: "long",
        year: yearNum,
      };
    case DateStyle.DATETIME:
      return {
        year: yearNum,
        month: monthNum,
        day: dayNum,
        hour: "numeric",
        minute: "numeric",
      };
    case DateStyle.TIME:
      return {
        hour: "2-digit",
        minute: "2-digit",
      };

    case DateStyle.MONTH_WORD:
      return { month: "long" };

    case DateStyle.MONTH_WORD_SHORT:
      return { month: "short" };
  }
}

export function endOfMonth(date: Date): DateTime {
  return DateTime.fromJSDate(
    new Date(date.getFullYear(), date.getMonth() + 1, 0),
  );
}
